import type { Room } from '@hollowveil/shared';

/**
 * Winterfell - "Tyrion et l'Estime de Soi"
 *
 * Personnage à aider: Tyrion Lannister
 * Problématique: Syndrome de l'imposteur extrême + rejet constant
 *
 * En spectral: On voit les "chaînes" qui le lient - les insultes intériorisées
 *
 * Ce que Côtelette apprend: L'adaptation - aider quelqu'un à voir sa valeur avec SES yeux
 */
export const roomWinterfellReveil: Room = {
  id: 'room_winterfell_reveil',
  name: 'Winterfell - Tyrion et l\'Estime de Soi',
  description: 'Les murailles froides de Winterfell. Dans une tour, un homme brillant se cache derrière son humour...',
  size: [24, 12, 24],
  floorStyle: 'stone',
  palette: 'night',
  biome: 'ice',
  initialWorld: 'real',
  hasSpectralLayer: true,
  weather: 'snow',
  weatherIntensity: 0.6,
  ambiance: {
    realWorld: {
      backgroundColor: '#1a2a3a',
      ambientLightColor: '#c8d8e8',
      ambientLightIntensity: 0.5,
      fogColor: '#2a3a4a',
      fogDensity: 0.04,
    },
    spectralWorld: {
      // On voit les chaînes et les blessures - plus doré/chaud pour le contraste
      backgroundColor: '#1a1a2e',
      ambientLightColor: '#ffd700',
      ambientLightIntensity: 0.35,
      fogColor: '#16213e',
      fogDensity: 0.05,
    },
  },
  objects: [
    // === LA TOUR DE TYRION ===
    {
      id: 'obj_tyrion_tower',
      name: 'Tour de la Bibliothèque',
      type: 'static',
      modelId: 'castle_tower',
      position: [12, 0, 18],
      rotation: [0, 0, 0],
      scale: [3, 4, 3],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
      collider: { type: 'box', size: [6, 6] },
    },

    // Tyrion
    {
      id: 'obj_tyrion',
      name: 'Tyrion Lannister',
      type: 'interactive',
      modelId: 'npc_tyrion',
      position: [12, 0, 16],
      rotation: [0, Math.PI, 0],
      scale: [0.8, 0.8, 0.8], // Plus petit que les autres
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {
        isNPC: true,
        state: 'cynical',
        dialogueId: 'tyrion_intro',
      },
      onInteract: [
        {
          type: 'showRiddle',
          params: {
            riddleId: 'tyrion_first_contact',
          },
        },
      ],
    },

    // Les chaînes de Tyrion (visibles en spectral)
    {
      id: 'obj_chain_monster',
      name: 'Chaîne "Monstre"',
      type: 'interactive',
      modelId: 'spectral_chain',
      position: [11, 1, 16],
      rotation: [0, 0.3, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {
        insult: 'MONSTRE',
        canBreak: true,
        requiresProof: 'compassion',
      },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: 'Une chaîne spectrale... gravée du mot "MONSTRE". Pour la briser, il faut prouver le contraire.',
          },
        },
      ],
    },
    {
      id: 'obj_chain_useless',
      name: 'Chaîne "Inutile"',
      type: 'interactive',
      modelId: 'spectral_chain',
      position: [13, 1.2, 16],
      rotation: [0, -0.2, 0],
      scale: [0.9, 0.9, 0.9],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {
        insult: 'INUTILE',
        canBreak: true,
        requiresProof: 'accomplishment',
      },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: 'Une chaîne gravée "INUTILE". Il faut prouver ses accomplissements pour la briser.',
          },
        },
      ],
    },
    {
      id: 'obj_chain_shame',
      name: 'Chaîne "Honte"',
      type: 'interactive',
      modelId: 'spectral_chain',
      position: [12, 1.5, 17],
      rotation: [0, 0.5, 0],
      scale: [0.85, 0.85, 0.85],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {
        insult: 'HONTE',
        canBreak: true,
        requiresProof: 'pride',
      },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: 'Une chaîne gravée "HONTE". Quelqu\'un doit avoir été fier de lui.',
          },
        },
      ],
    },
    {
      id: 'obj_chain_unloved',
      name: 'Chaîne "Non-Aimé"',
      type: 'interactive',
      modelId: 'spectral_chain',
      position: [11.5, 1.8, 15.5],
      rotation: [0, -0.4, 0],
      scale: [0.95, 0.95, 0.95],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {
        insult: 'NON-AIMÉ',
        canBreak: true,
        requiresProof: 'love',
      },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: 'Une chaîne gravée "NON-AIMÉ". Une preuve d\'amour pourrait la briser.',
          },
        },
      ],
    },
    {
      id: 'obj_chain_weak',
      name: 'Chaîne "Faible"',
      type: 'interactive',
      modelId: 'spectral_chain',
      position: [12.5, 2, 16.5],
      rotation: [0, 0.1, 0],
      scale: [1.1, 1.1, 1.1],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {
        insult: 'FAIBLE',
        canBreak: true,
        requiresProof: 'strength',
      },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: 'La plus grosse chaîne... gravée "FAIBLE". La vraie force n\'est pas physique.',
          },
        },
      ],
    },

    // === PREUVES DE VALEUR À TROUVER ===

    // Témoignage du serviteur
    {
      id: 'obj_servant_testimony',
      name: 'Serviteur Fidèle',
      type: 'interactive',
      modelId: 'npc_servant',
      position: [6, 0, 10],
      rotation: [0, Math.PI / 4, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {
        isNPC: true,
        hasProof: 'compassion',
      },
      onInteract: [
        {
          type: 'addToInventory',
          params: {
            itemId: 'proof_compassion',
            itemName: 'Témoignage de Compassion',
            itemDescription: '"Lord Tyrion m\'a sauvé la vie. Il a payé mes dettes et m\'a donné un travail quand personne ne voulait de moi."',
            itemIcon: 'scroll',
          },
        },
        {
          type: 'showMessage',
          params: {
            message: 'Serviteur: "Lord Tyrion ? Il m\'a sauvé la vie. Quand j\'étais à la rue, il a payé mes dettes. Un monstre ne ferait pas ça."',
          },
        },
        {
          type: 'hideObject',
          params: { objectId: 'obj_servant_testimony' },
        },
      ],
    },

    // Livre écrit par Tyrion
    {
      id: 'obj_tyrion_book',
      name: 'Livre de Tyrion',
      type: 'collectible',
      modelId: 'book',
      position: [12, 0.8, 20],
      rotation: [0, 0.3, 0],
      scale: [1.2, 1.2, 1.2],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
      onInteract: [
        {
          type: 'addToInventory',
          params: {
            itemId: 'proof_accomplishment',
            itemName: 'Traité de Stratégie',
            itemDescription: 'Un brillant traité militaire écrit par Tyrion. Il a sauvé Port-Réal grâce à ces stratégies.',
            itemIcon: 'scroll',
          },
        },
        {
          type: 'showMessage',
          params: {
            message: '"Traité sur la Défense de Port-Réal" par Tyrion Lannister. C\'est grâce à ce livre que la ville a survécu au siège.',
          },
        },
        {
          type: 'hideObject',
          params: { objectId: 'obj_tyrion_book' },
        },
      ],
    },

    // Lettre de quelqu'un qui l'admirait
    {
      id: 'obj_admiration_letter',
      name: 'Lettre d\'Admiration',
      type: 'collectible',
      modelId: 'scroll',
      position: [18, 0.5, 14],
      rotation: [0, -0.4, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
      onInteract: [
        {
          type: 'addToInventory',
          params: {
            itemId: 'proof_pride',
            itemName: 'Lettre d\'un Enfant',
            itemDescription: '"Cher Lord Tyrion, vous êtes mon héros. Vous avez prouvé qu\'on peut être grand même petit." - Podrick, 8 ans',
            itemIcon: 'scroll',
          },
        },
        {
          type: 'showMessage',
          params: {
            message: 'Une lettre d\'enfant: "Cher Lord Tyrion, vous êtes mon héros. Vous avez prouvé qu\'on peut être grand même petit."',
          },
        },
        {
          type: 'hideObject',
          params: { objectId: 'obj_admiration_letter' },
        },
      ],
    },

    // Preuve d'amour (de sa nièce/neveu)
    {
      id: 'obj_love_token',
      name: 'Dessin d\'Enfant',
      type: 'collectible',
      modelId: 'photo_frame',
      position: [8, 0.6, 18],
      rotation: [0, 0.2, 0],
      scale: [0.8, 0.8, 0.8],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
      onInteract: [
        {
          type: 'addToInventory',
          params: {
            itemId: 'proof_love',
            itemName: 'Dessin de Myrcella',
            itemDescription: 'Un dessin d\'enfant représentant Tyrion avec un grand cœur et écrit "Mon oncle préféré".',
            itemIcon: 'scroll',
          },
        },
        {
          type: 'showMessage',
          params: {
            message: 'Un dessin de Myrcella... Elle a dessiné Tyrion avec un énorme cœur et écrit "Mon oncle préféré".',
          },
        },
        {
          type: 'hideObject',
          params: { objectId: 'obj_love_token' },
        },
      ],
    },

    // Médaille de bravoure
    {
      id: 'obj_bravery_medal',
      name: 'Médaille de Bravoure',
      type: 'collectible',
      modelId: 'medal',
      position: [16, 0.7, 8],
      rotation: [0, 0.1, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
      onInteract: [
        {
          type: 'addToInventory',
          params: {
            itemId: 'proof_strength',
            itemName: 'Médaille de Bravoure',
            itemDescription: 'Décernée à Tyrion pour avoir mené la charge à la Bataille de la Néra. La vraie force est le courage.',
            itemIcon: 'gem',
          },
        },
        {
          type: 'showMessage',
          params: {
            message: 'Une médaille de bravoure... décernée à Tyrion pour avoir mené la charge alors que tous fuyaient.',
          },
        },
        {
          type: 'hideObject',
          params: { objectId: 'obj_bravery_medal' },
        },
      ],
    },

    // Le Miroir de Vérité (fragments à reconstituer)
    {
      id: 'obj_truth_mirror',
      name: 'Miroir de Vérité',
      type: 'interactive',
      modelId: 'mirror_broken',
      position: [12, 0, 22],
      rotation: [0, Math.PI, 0],
      scale: [2, 2, 2],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {
        broken: true,
        fragments: 5,
      },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: 'Un miroir brisé... En spectral, il montre la vérité. Si on le répare, Tyrion pourrait se voir tel qu\'il est vraiment.',
          },
        },
      ],
    },

    // === DÉCORS ===

    // Remparts
    {
      id: 'obj_rampart_1',
      name: 'Rempart',
      type: 'static',
      modelId: 'castle_wall',
      position: [0, 0, 12],
      rotation: [0, 0, 0],
      scale: [1, 3, 24],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
      collider: { type: 'box', size: [2, 24] },
    },
    {
      id: 'obj_rampart_2',
      name: 'Rempart',
      type: 'static',
      modelId: 'castle_wall',
      position: [24, 0, 12],
      rotation: [0, 0, 0],
      scale: [1, 3, 24],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
      collider: { type: 'box', size: [2, 24] },
    },

    // Neige et givre
    {
      id: 'obj_snow_pile_1',
      name: 'Tas de Neige',
      type: 'static',
      modelId: 'snow_pile',
      position: [4, 0, 6],
      rotation: [0, 0.2, 0],
      scale: [1.5, 1, 1.5],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    {
      id: 'obj_snow_pile_2',
      name: 'Tas de Neige',
      type: 'static',
      modelId: 'snow_pile',
      position: [20, 0, 8],
      rotation: [0, -0.3, 0],
      scale: [1.8, 1.2, 1.8],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    {
      id: 'obj_snow_pile_3',
      name: 'Tas de Neige',
      type: 'static',
      modelId: 'snow_pile',
      position: [8, 0, 22],
      rotation: [0, 0.5, 0],
      scale: [1.3, 0.8, 1.3],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },

    // Arbres enneigés
    {
      id: 'obj_snow_tree_1',
      name: 'Arbre Enneigé',
      type: 'static',
      modelId: 'pine_tree_snow',
      position: [3, 0, 12],
      rotation: [0, 0.2, 0],
      scale: [1.5, 1.5, 1.5],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    {
      id: 'obj_snow_tree_2',
      name: 'Arbre Enneigé',
      type: 'static',
      modelId: 'pine_tree_snow',
      position: [21, 0, 14],
      rotation: [0, -0.4, 0],
      scale: [1.8, 1.8, 1.8],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    {
      id: 'obj_snow_tree_3',
      name: 'Arbre Enneigé',
      type: 'static',
      modelId: 'pine_tree_snow',
      position: [16, 0, 22],
      rotation: [0, 0.1, 0],
      scale: [1.3, 1.3, 1.3],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },

    // Barberois (Weirwood tree) - Arbre sacré
    {
      id: 'obj_weirwood',
      name: 'Barral',
      type: 'static',
      modelId: 'weirwood_tree',
      position: [6, 0, 20],
      rotation: [0, 0, 0],
      scale: [2, 2.5, 2],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {
        sacred: true,
        glows: true,
        color: '#ffffff',
      },
    },

    // Feu de camp pour le confort
    {
      id: 'obj_campfire',
      name: 'Feu de Camp',
      type: 'static',
      modelId: 'campfire',
      position: [10, 0, 10],
      rotation: [0, 0, 0],
      scale: [1.2, 1.2, 1.2],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {
        glows: true,
        color: '#ff6600',
      },
    },

    // Bancs en pierre couverts de neige
    {
      id: 'obj_stone_bench_1',
      name: 'Banc de Pierre',
      type: 'static',
      modelId: 'stone_bench',
      position: [8, 0, 8],
      rotation: [0, Math.PI / 4, 0],
      scale: [1.2, 1, 1.2],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    {
      id: 'obj_stone_bench_2',
      name: 'Banc de Pierre',
      type: 'static',
      modelId: 'stone_bench',
      position: [14, 0, 6],
      rotation: [0, -Math.PI / 6, 0],
      scale: [1.2, 1, 1.2],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },

    // Tonneau de vin (pour Tyrion)
    {
      id: 'obj_wine_barrel',
      name: 'Tonneau de Vin',
      type: 'static',
      modelId: 'barrel',
      position: [14, 0, 18],
      rotation: [0, 0.3, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },

    // Trône de pierre
    {
      id: 'obj_stone_throne',
      name: 'Trône de Pierre',
      type: 'static',
      modelId: 'throne',
      position: [20, 0, 20],
      rotation: [0, -Math.PI / 4, 0],
      scale: [1.5, 1.5, 1.5],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },

    // Cristaux de glace
    {
      id: 'obj_ice_crystal_1',
      name: 'Cristal de Glace',
      type: 'static',
      modelId: 'ice_crystal',
      position: [2, 0, 6],
      rotation: [0, 0.3, 0],
      scale: [1.5, 2, 1.5],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: { glows: true, color: '#87ceeb' },
    },
    {
      id: 'obj_ice_crystal_2',
      name: 'Cristal de Glace',
      type: 'static',
      modelId: 'ice_crystal',
      position: [22, 0, 18],
      rotation: [0, -0.5, 0],
      scale: [1.2, 1.8, 1.2],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: { glows: true, color: '#b0e0e6' },
    },

    // Torches sur les murs
    {
      id: 'obj_torch_1',
      name: 'Torche',
      type: 'static',
      modelId: 'torch_stand',
      position: [2, 0, 10],
      rotation: [0, Math.PI / 2, 0],
      scale: [1, 1.5, 1],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: { glows: true, color: '#ff6600' },
    },
    {
      id: 'obj_torch_2',
      name: 'Torche',
      type: 'static',
      modelId: 'torch_stand',
      position: [22, 0, 10],
      rotation: [0, -Math.PI / 2, 0],
      scale: [1, 1.5, 1],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: { glows: true, color: '#ff6600' },
    },

    // Bannière des Stark
    {
      id: 'obj_banner_stark',
      name: 'Bannière des Stark',
      type: 'static',
      modelId: 'banner',
      position: [12, 3, 23],
      rotation: [0, Math.PI, 0],
      scale: [2, 3, 1],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: { color: '#666666', emblem: 'direwolf' },
    },

    // Rochers enneigés
    {
      id: 'obj_snow_rock_1',
      name: 'Rocher Enneigé',
      type: 'static',
      modelId: 'rock',
      position: [4, 0, 2],
      rotation: [0, 0.4, 0],
      scale: [2, 1.5, 2],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    {
      id: 'obj_snow_rock_2',
      name: 'Rocher Enneigé',
      type: 'static',
      modelId: 'rocks',
      position: [18, 0, 2],
      rotation: [0, -0.2, 0],
      scale: [2.5, 2, 2.5],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },

    // Échafaudage d'entraînement
    {
      id: 'obj_weapon_rack',
      name: 'Râtelier d\'Armes',
      type: 'static',
      modelId: 'weapon_rack',
      position: [4, 0, 8],
      rotation: [0, Math.PI / 4, 0],
      scale: [1.2, 1.2, 1.2],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
    },

    // Puits de la cour
    {
      id: 'obj_well',
      name: 'Puits',
      type: 'static',
      modelId: 'well',
      position: [10, 0, 6],
      rotation: [0, 0, 0],
      scale: [1.3, 1.3, 1.3],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },

    // Portail retour vers le Sanctuaire
    {
      id: 'obj_portal_back',
      name: 'Portail vers le Sanctuaire',
      type: 'interactive',
      modelId: 'portal_sanctuary',
      position: [12, 0, 1],
      rotation: [0, 0, 0],
      scale: [2, 3, 0.5],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {
        portalTo: 'room_sanctuary',
        portalColor: '#9d8cff',
      },
      onInteract: [
        {
          type: 'loadRoom',
          params: { roomId: 'room_sanctuary' },
        },
      ],
    },

    // L'Oracle déguisé en barde
    {
      id: 'obj_oracle_appearance',
      name: 'L\'Oracle Chevelu',
      type: 'interactive',
      modelId: 'npc_oracle_bard',
      position: [4, 0, 16],
      rotation: [0, Math.PI / 4, 0],
      scale: [0.9, 0.9, 0.9],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {
        isNPC: true,
        dialogueId: 'oracle_winterfell_hint',
      },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: 'Oracle: *déguisé en barde, gratte une lyre faux* "♪ L\'homme petit par la taille... ♪ ...est un géant par le cœur ! ♪" Tyrion: "C\'est touchant. Et terriblement faux comme musique."',
          },
        },
      ],
    },
  ],
  puzzles: [
    // Collecter les preuves de valeur
    {
      id: 'puzzle_collect_proofs',
      name: 'Les Preuves de Valeur',
      type: 'collect',
      state: 'active',
      description: 'Trouve les 5 preuves de la valeur de Tyrion pour briser ses chaînes.',
      hint: 'Cherche des témoignages, des lettres, des objets qui prouvent ce qu\'il a accompli.',
      linkedObjectIds: [
        'obj_servant_testimony',
        'obj_tyrion_book',
        'obj_admiration_letter',
        'obj_love_token',
        'obj_bravery_medal',
      ],
      solution: {
        type: 'items',
        expected: ['proof_compassion', 'proof_accomplishment', 'proof_pride', 'proof_love', 'proof_strength'],
      },
      onSolve: [
        {
          type: 'showMessage',
          params: {
            message: 'Tu as rassemblé toutes les preuves. Il est temps de briser les chaînes de Tyrion.',
          },
        },
      ],
    },
    // Briser les chaînes
    {
      id: 'puzzle_break_chains',
      name: 'Briser les Chaînes',
      type: 'placement',
      state: 'locked',
      description: 'Associe chaque preuve à la chaîne correspondante pour la briser.',
      requiredWorld: 'spectral',
      linkedObjectIds: [
        'obj_chain_monster',
        'obj_chain_useless',
        'obj_chain_shame',
        'obj_chain_unloved',
        'obj_chain_weak',
      ],
      solution: {
        type: 'state',
        expected: {
          monster: 'compassion',
          useless: 'accomplishment',
          shame: 'pride',
          unloved: 'love',
          weak: 'strength',
        },
      },
      onSolve: [
        {
          type: 'showMessage',
          params: {
            message: 'Les chaînes tombent une à une... Tyrion peut enfin se voir tel qu\'il est vraiment.',
          },
        },
        {
          type: 'addToInventory',
          params: {
            itemId: 'epee_perseverance',
            itemName: 'Épée de la Persévérance',
            itemDescription: 'Une dague de Tyrion. "La vraie force n\'est pas dans les muscles, mais dans le refus d\'abandonner."',
            itemIcon: 'key',
          },
        },
      ],
    },
  ],
  connections: [
    {
      targetRoomId: 'room_sanctuary',
      position: [12, 0, 1],
      direction: 'north',
      locked: false,
    },
  ],
  npcs: [
    // Ghost (le loup de Jon Snow)
    {
      id: 'npc_ghost',
      type: 'wolf',
      name: 'Ghost',
      position: [18, 0, 10],
      scale: 1.5,
      idleAnimation: 'sit',
      petAnimation: 'nuzzle',
      petRadius: 3,
      petCooldown: 4000,
      isPettable: true,
      visibleInWorld: 'both',
    },
    // Corbeau messager
    {
      id: 'npc_raven',
      type: 'owl', // Utilise owl comme base
      name: 'Corbeau',
      position: [8, 2, 12],
      scale: 0.8,
      idleAnimation: 'fly',
      petAnimation: 'chirp',
      petRadius: 2,
      petCooldown: 3000,
      isPettable: true,
      visibleInWorld: 'both',
    },
    // Cheval de l'écurie
    {
      id: 'npc_horse',
      type: 'deer', // Utilise deer comme base
      name: 'Cheval de Guerre',
      position: [20, 0, 4],
      scale: 1.8,
      idleAnimation: 'stand',
      petAnimation: 'nuzzle',
      petRadius: 3,
      petCooldown: 4000,
      isPettable: true,
      visibleInWorld: 'real',
    },
    // Chien de berger
    {
      id: 'npc_sheepdog',
      type: 'wolf',
      name: 'Chien de Berger',
      position: [6, 0, 4],
      scale: 1,
      idleAnimation: 'stand',
      petAnimation: 'happy',
      petRadius: 2.5,
      petCooldown: 3000,
      isPettable: true,
      visibleInWorld: 'real',
    },
  ],
};
